import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { Location } from '@/types/itineraryTypes';

interface Header {
  title: string;
  location: Location;
}

interface HeaderStore {
  header: Header;
  title: string;
  tags: string[];
  mainHeader: Header;
  setHeader: (header: Header) => void;
  setMainHeader: (header: Header) => void;
  setTitle: (title: string) => void;
  setTags: (tags: string[]) => void;
  resetHeader: () => void;
  clearTags: () => void;
  initializeRandomTags: () => void;
}

export const ALL_TAGS = [
  // India - 25 Most Famous Destinations
  'Best time to visit Taj Mahal for perfect photos',
  'Quietest beaches in South Goa for couples',
  'Which backwater route is best in Alleppey?',
  'Best lake-view resorts in Munnar for couples',
  'Luxury desert camps near Jaisalmer dunes',
  'Best rooftop restaurants with fort view in Jaipur',
  'Shopping streets in Udaipur for handmade crafts',
  'Evening Ganga aarti spots in Varanasi',
  'Where to stay in Rishikesh for peaceful Ganga view',
  'Best cafes in Manali with mountain views',
  'Is the Spiti Valley road trip doable in winter?',
  'How to plan Srinagar to Leh road trip',
  'Best time to visit Pangong Lake',
  'Weekend trips from Bangalore within 200 km',
  'Best homestays in Coorg coffee plantations',
  'How to visit Hampi ruins efficiently',
  'Is Pondicherry good for solo travel?',
  'Best time for Darjeeling toy train ride',
  'Which is better: Havelock or Neil Island?',
  'Best scuba diving spots for beginners',
  'How to do a Lonavala trip by train',
  'Best sunrise points in Nainital and Mukteshwar',
  'Best time to see living root bridges in Meghalaya',
  'Best wildlife safaris in Bandhavgarh and Kanha',
  'How to reach Valley of Flowers from Haridwar',

  // International - 25 Most Famous Destinations
  'Where to stay in Montmartre with Eiffel Tower views',
  'Best cafes in Paris with riverfront or garden seating',
  'Hidden tapas bars in Barcelona with local crowd',
  'Best sunset viewpoints in Oia without the crowds',
  'How to island-hop in the Cyclades on a budget',
  'Best cave hotels in Cappadocia for balloon views',
  'Best rooftop cafes in Istanbul for sunset',
  'Best Ubud stays with private jungle pools',
  'Is Mount Batur sunrise trek worth the early start?',
  'How to reach Railay Beach from Krabi or Ao Nang',
  'Best rooftop bars in Bangkok under $10 entry',
  'Hidden temples in Kyoto with fewer tourists',
  'Sakura spots in Tokyo that locals love',
  'Best time to visit Everest Base Camp',
  'How to explore Singapore on a $50/day budget',
  'Scenic day hikes near Queenstown',
  'Road trip plan from Christchurch to Milford Sound',
  'Scenic road trip stops on Great Ocean Road',
  'Hidden lakes near Banff for sunrise paddles',
  'Where to stay in NYC with skyline views under $200',
  'Scenic Southwest road trip stops (Utah, Arizona)',
  "Best cenotes near Tulum that aren't crowded",
  'Most scenic train routes from Lucerne to Zermatt',
  'Best desert camps in Merzouga for stargazing',
  'Where to stay in Cape Town for Table Mountain views',
];

// Function to get random tags
export const getRandomTags = (count: number = 5): string[] => {
  const shuffled = [...ALL_TAGS].sort(() => Math.random() - 0.5);
  return shuffled.slice(0, count);
};

export const useHeaderStore = create<HeaderStore>()(
  persist(
    set => ({
      header: { title: '', location: {} as Location },
      mainHeader: { title: '', location: {} as Location },
      title: '',
      tags: [],

      setHeader: header => set({ header }),
      setTitle: title => set({ title }),
      setTags: tags => set({ tags: tags ?? [] }),
      setMainHeader: mainHeader =>
        set({
          mainHeader: {
            title: mainHeader.title,
            location: mainHeader.location,
          },
        }),
      resetHeader: () =>
        set({
          header: { title: '', location: {} as Location },
          mainHeader: { title: '', location: {} as Location },
          title: '',
          tags: getRandomTags(),
        }),

      clearTags: () => set({ tags: [] }),
      initializeRandomTags: () => set({ tags: getRandomTags() }),
    }),
    {
      name: 'header-store',
      skipHydration: true,
    }
  )
);
